import React, { useState } from 'react';
import { DefaultZIndexes, ZIndexLayer, usePlotArea, useXAxisDomain, useXAxisScale } from 'recharts';
import { Note } from '../types';
import NoteHoverCard from './NoteHoverCard';
import { noteAuthor } from '../utils/noteAuthor';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from './pilot-ui/popover';
import Button from './ui/Button';

export interface SeriesNotes {
  deviceId: number;
  color: string;
  notes: Note[];
}

interface NoteMarkerOverlayProps {
  seriesNotes: SeriesNotes[];
  onImageClick: (noteId: number) => void;
  maxSnapMs?: number;
}

const DEFAULT_MAX_SNAP_MS = 48 * 3600 * 1000;
// Hover cards must sit above reference lines and their labels.
const NOTE_OVERLAY_Z_INDEX = DefaultZIndexes.label + 1;
const MARKER_RADIUS = 6;
const MARKER_HIT_SIZE = 24;
const CARD_TOP_OFFSET = 4;
const MARKER_BRIDGE_PADDING = 4;

const NoteMarkerOverlay: React.FC<NoteMarkerOverlayProps> = ({
  seriesNotes,
  onImageClick,
  maxSnapMs = DEFAULT_MAX_SNAP_MS,
}) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [openedKey, setOpenedKey] = useState<string | null>(null);
  const [suppressedHoverKey, setSuppressedHoverKey] = useState<string | null>(null);
  const domain = useXAxisDomain();
  const scale = useXAxisScale();
  const plotArea = usePlotArea();

  if (!domain || !scale || !plotArea || domain.length === 0 || !domain.every((value) => typeof value === 'string')) return null;

  const domainMs = domain.map((value) => Date.parse(String(value)));
  const plotTop = plotArea.y;
  const plotBottom = plotArea.y + plotArea.height;
  const plotLeft = plotArea.x;
  const plotRight = plotArea.x + plotArea.width;

  type Marker = { key: string; x: number; note: Note; color: string };
  const markers: Marker[] = [];

  for (const sn of seriesNotes) {
    for (const note of sn.notes) {
      const noteMs = Date.parse(note.created_at);
      if (!Number.isFinite(noteMs)) continue;
      let bestIdx = -1;
      let bestDiff = Infinity;
      for (let i = 0; i < domainMs.length; i++) {
        const d = domainMs[i];
        if (!Number.isFinite(d)) continue;
        const diff = Math.abs(d - noteMs);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i;
        }
      }
      if (bestIdx < 0 || bestDiff > maxSnapMs) {
        if (process.env.NODE_ENV !== 'production') {
          // Keep skipped-marker diagnostics visible while investigating chart data.
          // eslint-disable-next-line no-console
          console.debug(
            `[NoteMarkerOverlay] skipped note ${note.id}: nearest domain ${
              Number.isFinite(bestDiff) ? Math.round(bestDiff / 3600000) + 'h' : 'N/A'
            } away (>${Math.round(maxSnapMs / 3600000)}h)`,
          );
        }
        continue;
      }
      const domainValue = domain[bestIdx];
      const px = scale(domainValue, { position: 'middle' });
      if (px === undefined || !Number.isFinite(px)) continue;
      if (px < plotLeft - 1 || px > plotRight + 1) continue;
      markers.push({ key: `${sn.deviceId}:${note.id}`, x: px, note, color: sn.color });
    }
  }

  if (markers.length === 0) return null;

  const closeWhenLeaving = (
    event: React.FocusEvent<SVGGElement> | React.MouseEvent<SVGGElement>,
    key: string,
  ) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    setHoveredKey((current) => (current === key ? null : current));
    setSuppressedHoverKey((current) => (current === key ? null : current));
  };

  const CARD_WIDTH = 320;
  const CARD_HEIGHT = 280;

  return (
    <ZIndexLayer zIndex={NOTE_OVERLAY_Z_INDEX}>
    <g className="note-marker-overlay">
      {markers.map((m) => {
        const hovered = hoveredKey === m.key;
        const flipX = m.x + CARD_WIDTH > plotRight;
        const cardX = flipX ? Math.max(plotLeft, m.x - CARD_WIDTH) : m.x + 8;
        const cardY = plotTop + CARD_TOP_OFFSET;
        const bridgeX = Math.min(m.x - MARKER_RADIUS, cardX);
        const bridgeRight = Math.max(m.x + MARKER_RADIUS, cardX + CARD_WIDTH);
        return (
          <g
            key={m.key}
            onMouseLeave={(event) => closeWhenLeaving(event, m.key)}
            onBlur={(event) => closeWhenLeaving(event, m.key)}
          >
            <line
              x1={m.x}
              x2={m.x}
              y1={plotTop}
              y2={plotBottom}
              stroke={m.color}
              strokeDasharray="3 3"
              strokeOpacity={hovered ? 0.8 : 0.3}
              strokeWidth={hovered ? 2 : 1}
              style={{ pointerEvents: 'none' }}
            />
            {hovered && (
              <rect
                x={bridgeX}
                y={plotTop - 2 * MARKER_RADIUS}
                width={bridgeRight - bridgeX}
                height={2 * MARKER_RADIUS + MARKER_BRIDGE_PADDING}
                fill="transparent"
                aria-hidden="true"
              />
            )}
            <Popover open={openedKey === m.key} onOpenChange={(open) => {
              setOpenedKey(open ? m.key : null);
              if (!open) {
                setHoveredKey(null);
                setSuppressedHoverKey(m.key);
              }
            }}>
              <foreignObject
                x={m.x - MARKER_HIT_SIZE / 2}
                y={plotTop - MARKER_RADIUS - MARKER_HIT_SIZE / 2}
                width={MARKER_HIT_SIZE}
                height={MARKER_HIT_SIZE}
                style={{ overflow: 'visible' }}
              >
                <PopoverTrigger render={<Button
                  variant="icon"
                  className="size-6 rounded-full p-0"
                  aria-label={`Note from ${noteAuthor(m.note)} on ${m.note.created_at}`}
                  title={`Open details for note from ${noteAuthor(m.note)}`}
                  onMouseEnter={() => {
                    setSuppressedHoverKey(null);
                    setHoveredKey(m.key);
                  }}
                  onFocus={() => {
                    if (suppressedHoverKey !== m.key) setHoveredKey(m.key);
                  }}
                />}>
                  <span className="size-3 rounded-full border border-white" style={{ backgroundColor: m.color }} aria-hidden="true" />
                </PopoverTrigger>
              </foreignObject>
              <PopoverContent align="start" className="w-80">
                <PopoverTitle>Note details</PopoverTitle>
                <NoteHoverCard note={m.note} deviceColor={m.color} onImageClick={onImageClick} />
              </PopoverContent>
            </Popover>
            {hovered && openedKey !== m.key && (
              <foreignObject
                x={cardX}
                y={cardY}
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                style={{ overflow: 'visible', pointerEvents: 'auto' }}
              >
                <div
                  onMouseEnter={() => setHoveredKey(m.key)}
                >
                  <NoteHoverCard
                    note={m.note}
                    deviceColor={m.color}
                    onImageClick={onImageClick}
                  />
                </div>
              </foreignObject>
            )}
          </g>
        );
      })}
    </g>
    </ZIndexLayer>
  );
};

export default NoteMarkerOverlay;
